import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Circle,
  Divider,
  Flex,
  HStack,
  Stack,
  Text,
  useBreakpointValue,
  useToken,
} from '@chakra-ui/react';

import ChildLink from './ChildLink';
import { HUB_SESSION_BANNER_HEIGHT } from '@/client/components/shared/messaging/HubSessionBanner';
import ParentLink from './ParentLink';
import { hexToRGBA } from '@/client/utils/hexToRGBA';
import { useIsActiveHubSession } from '@/client/services/state/hubSessionStore';
import { useSideNavigation } from './useSideNavigation';
import { useState } from 'react';

interface SideNavProps {
  initialOpen?: boolean;
}

export default function SideNav({ initialOpen = false }: SideNavProps) {
  const { navLinks, parentLinkStyles } = useSideNavigation();
  const [navOpen, setNavOpen] = useState(initialOpen);
  const [brandColor] = useToken('colors', ['brand.primary']);
  const isHubSession = useIsActiveHubSession();

  const isDesktop = useBreakpointValue({ base: false, lg: true });

  return (
    <Box
      backgroundColor="white"
      position="fixed"
      // Shifted down while the hub session banner shows (matches MainNav)
      top={
        isHubSession
          ? {
              base: `calc(30px + ${HUB_SESSION_BANNER_HEIGHT})`,
              md: `calc(80px + ${HUB_SESSION_BANNER_HEIGHT})`,
            }
          : { base: '30px', md: '80px' }
      }
      bottom={0}
      zIndex={1200}
      width={initialOpen ? 'full' : '80px'}
      transition=".2s ease all"
      transform="translate(0)"
      overflowY="auto"
      paddingTop={14}
      paddingX={2}
      boxShadow={{ base: 'none', lg: 'lg' }}
      _hover={{ base: {}, lg: { width: '224px' } }}
      onMouseEnter={isDesktop ? () => setNavOpen(true) : undefined}
      onMouseLeave={
        isDesktop
          ? () => {
              if (!initialOpen) {
                setNavOpen(false);
              }
            }
          : undefined
      }
    >
      <Stack overflow="hidden" spacing={0}>
        <Accordion
          index={!navOpen ? 99 : undefined} // ! Handles closing the accordion when the nav is closed
          allowToggle
          width="full"
        >
          {navLinks.map((link) => {
            if (!link.enabled) return null;

            if (link.divider)
              return (
                <Divider
                  key={'divider_' + (link.name || 'default')}
                  width="full"
                  marginY={2}
                  borderWidth={1}
                  borderColor="neutral.100"
                />
              );

            return link.children ? (
              <AccordionItem border="none" width="full">
                {({ isExpanded }) => (
                  <>
                    <h6>
                      <AccordionButton
                        sx={
                          isExpanded
                            ? {
                                ...parentLinkStyles,
                                backgroundColor: hexToRGBA(brandColor, 0.1),
                                color: 'neutral.1500',
                                fontWeight: 600,
                              }
                            : parentLinkStyles
                        }
                      >
                        <Flex
                          flex="1"
                          width="full"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <HStack spacing={2}>
                            <Circle
                              backgroundColor={hexToRGBA(brandColor, 0.1)}
                              color="neutral.1500"
                              size="32px"
                            >
                              {link.icon}
                            </Circle>

                            {navOpen && <Text>{link.name}</Text>}
                          </HStack>

                          {navOpen && <AccordionIcon boxSize={6} color="neutral.1500" />}
                        </Flex>
                      </AccordionButton>
                    </h6>

                    <AccordionPanel marginTop={0} paddingX={0}>
                      <Stack
                        height={(link.children?.filter((child) => child.enabled).length || 0) * 35}
                        spacing={0}
                      >
                        {link.children?.map((child) => {
                          if (!child.enabled) return null;

                          return (
                            <ChildLink
                              key={'childlink_' + (child.name || 'default')}
                              navLink={child}
                            />
                          );
                        })}
                      </Stack>
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            ) : (
              <ParentLink navLink={link} navOpen={navOpen} />
            );
          })}
        </Accordion>
      </Stack>
    </Box>
  );
}
